import { Field, FieldProps } from 'formik'

import React, { useState, useEffect, useContext } from 'react'
import { variantsType } from '../../styles/animation'
import AnimateWithVariants from './AnimateWithVariants'
import { FieldsContext } from '../form/Form'

export type FieldContainerChildrenProps = FieldProps['field'] & { error: boolean } & {
   helperText: string
   name: string
   disabled?: boolean
}

interface Props {
   children: (props: FieldContainerChildrenProps) => React.ReactNode
   name: string
   highlight?: boolean
}

const FieldContainer: React.FC<Props> = ({ children, name, highlight }) => {
   const { errorsArray, loading } = useContext(FieldsContext)
   // check if field has error from server
   const fieldGotErrorFromServer: boolean = errorsArray && errorsArray.includes(name) ? true : false

   return (
      <Field name={name}>
         {({ field, meta, form }: FieldProps) => {
            const [animationVariant, setAnimationVariant] = useState<variantsType>('default')
            const [showErrorFromServer, setShowErrorFromServer] = useState<boolean>(false)

            // show error from server if field has error from server
            useEffect(() => {
               fieldGotErrorFromServer && setShowErrorFromServer(true)
            }, [fieldGotErrorFromServer])

            // hide error from server if field is changed
            useEffect(() => {
               showErrorFromServer && setShowErrorFromServer(false)
            }, [form.values[name]])

            // show error if field is touched or form is submitting or field has error from server
            const showError: boolean =
               (meta.error && meta.touched) ||
               (form.errors[name] && form.isSubmitting) ||
               showErrorFromServer
                  ? true
                  : false

            const readyProps = {
               ...field,
               error: showError,
               helperText: showError && meta.error,
               name,
               disabled: loading,
            } as FieldContainerChildrenProps

            useEffect(() => {
               showError && setAnimationVariant('shake')
            }, [showError])

            return (
               <AnimateWithVariants
                  animate={animationVariant}
                  onAnimationComplete={() => setAnimationVariant('default')}
                  className={'formik-field-container' + (highlight ? ' highlighted' : '')}
               >
                  {children(readyProps)}
               </AnimateWithVariants>
            )
         }}
      </Field>
   )
}

export default FieldContainer
